import { type ChartOptions } from "chart.js";
import { mean } from "lodash";

import {
  type AdoptableMetrics,
  type DaySelection,
} from "@features/universal-benchmarks/configurator/types";
import {
  adjustMetricsGrossRevRow,
  normalizeMetricValue,
} from "@features/universal-benchmarks/configurator/utils";
import { type User } from "@internal/user";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  convertStartDateToTimeZone,
  type CurrencyCode,
  formatting,
} from "@north-beam/nb-common";

interface DataPoint {
  x: Date;
  y: number;
}

function getCssVariable(name: string, from: HTMLElement = document.body) {
  return getComputedStyle(from).getPropertyValue(name).trim();
}

export function getChartOptions({
  code,
}: {
  code: CurrencyCode;
}): ChartOptions<"line"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animations: undefined,
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
    },
    layout: {
      padding: { bottom: -2 },
    },
    scales: {
      x: {
        grid: {
          drawOnChartArea: true,
          drawBorder: false,
          drawTicks: false,
          color: (context) => {
            if (
              context.index === 0 ||
              context.index === context.chart.scales.y.ticks.length - 1
            ) {
              return "";
            }
            return getCssVariable("--v2-border-tertiary");
          },
          borderDash: [2, 2],
        },
        ticks: {
          padding: 13,
          maxRotation: 45,
          autoSkip: true,
          color: getCssVariable("--v2-text-primary"),
          font: {
            size: 10,
          },
        },
        type: "timeseries",
        time: {
          unit: "month",
          displayFormats: {
            month: "MMM",
          },
        },
      },
      y: {
        position: "right",
        beginAtZero: true,
        grid: {
          drawOnChartArea: true,
          drawBorder: false,
          drawTicks: false,
          color: (context) => {
            if (
              context.index === 0 ||
              context.index === context.chart.scales.y.ticks.length - 1
            ) {
              return "";
            }
            return getCssVariable("--v2-border-tertiary");
          },
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 6,
          color: getCssVariable("--v2-text-primary"),
          font: {
            size: 10,
          },
          callback: (value: number | string) => {
            return formatting.formatCurrency.approximate(Number(value), {
              code,
            });
          },
        },
        title: {
          display: true,
          text: "Attributed Rev",
          font: {
            size: 12,
            weight: "600",
          },
        },
      },
    },
  };
}

export function generateChartData({
  data,
  adoptableBenchmarksMetrics,
  metric,
  daysSelection,
  user,
}: {
  data: GetOverviewMetricsReport_me_overviewMetricsReport_rows;
  adoptableBenchmarksMetrics: AdoptableMetrics;
  metric: SalesPageMetric;
  daysSelection: DaySelection[];
  user: User;
}) {
  const labels = data.metrics.map((e) =>
    convertStartDateToTimeZone(e.date, user.timezone),
  );

  const data1: DataPoint[] = [];
  const data2: DataPoint[] = [];

  const excludedDays = daysSelection
    .filter((e) => e.isExcluded || e.willBeExcluded)
    .map((e) => e.day);

  const metricValues = [];
  const roasValues = [];

  for (const metricPoint of data.metrics) {
    if (!excludedDays.includes(metricPoint.date)) {
      metricValues.push(normalizeMetricValue(metric.calculate(metricPoint)));
      roasValues.push(
        normalizeMetricValue(
          adoptableBenchmarksMetrics.roas.calculate(metricPoint),
        ),
      );
    }
  }

  const avgMetricValue = mean(metricValues);
  const avgRoasValue = mean(roasValues);

  for (const [index, metricPoint] of data.metrics.entries()) {
    const metricValue = normalizeMetricValue(metric.calculate(metricPoint));
    let hit = false;
    if (metric.isPositiveChangeGood && metricValue >= avgMetricValue) {
      hit = true;
    }
    if (!metric.isPositiveChangeGood && metricValue <= avgMetricValue) {
      hit = true;
    }

    const metricPointGross = adjustMetricsGrossRevRow(
      metricPoint,
      user.ecomAccountingSettings,
    );

    const rev = adoptableBenchmarksMetrics.rev.calculate(metricPointGross);
    let potentialRev = 0;

    if (hit) {
      potentialRev = rev;
    } else {
      const spend =
        adoptableBenchmarksMetrics.spend.calculate(metricPointGross);
      potentialRev = spend * avgRoasValue;
    }

    data1.push({
      x: labels[index],
      y: rev,
    });

    data2.push({
      x: labels[index],
      y: potentialRev,
    });
  }

  return { labels: labels, datasets: [{ data: data1 }, { data: data2 }] };
}

export function generateChartDataset(
  data: ReturnType<typeof generateChartData>,
) {
  const datasets = [];

  for (const [index, dataset] of data.datasets.entries()) {
    const color =
      index === 0
        ? getCssVariable("--v2-priority-2")
        : getCssVariable("--v2-priority-9");

    datasets.push({
      ...dataset,
      id: index,
      yAxisID: "y",
      pointRadius: 0,
      borderColor: color,
      borderWidth: 1,
      order: color === getCssVariable("--v2-priority-2") ? 2 : 1,
      tension: 0.3,
    });
  }

  return { ...data, datasets };
}
